import React, { useState } from 'react'
import {
  Brain, Send, CheckCircle, XCircle, AlertTriangle, Lightbulb,
  ArrowRight, History, ChevronDown, ChevronUp, Star, Target,
  BarChart2, BookOpen, RefreshCw, Award
} from 'lucide-react'
import MisconceptionAlert from '../components/MisconceptionAlert'
import OntologyTag from '../components/OntologyTag'
import { useProgressStore } from '../store/progressStore'
import type { TutorAnalysisResult } from '../types'

// ────────────────────────────────────────────────────────────────────────────
// 6가지 데모 시나리오 (오개념 M01~M08 포함)
// ────────────────────────────────────────────────────────────────────────────
const DEMO_SCENARIOS = [
  {
    label: '📌 시나리오 1: 기울기·y절편 혼동 (M03)',
    emoji: '📌',
    category: '기울기 이해',
    step: 'LS05',
    question: 'y = 2x + 5에서 기울기와 y절편을 구하시오.',
    answer: '기울기 = 5, y절편 = 2',
    wrongAnswer: '기울기 = 5, y절편 = 2',
    note: '기울기와 y절편을 바꿔서 답한 경우',
  },
  {
    label: '📌 시나리오 2: 정비례 ≠ 1차 함수 혼동 (M04)',
    emoji: '📌',
    category: '1차 함수 개념',
    step: 'LS07',
    question: 'y = 3x는 1차 함수인가?',
    answer: '아니오, 정비례라서 1차 함수가 아닙니다.',
    wrongAnswer: '아니오, 정비례라서 1차 함수가 아닙니다.',
    note: '정비례 함수가 1차 함수에 포함됨을 모르는 경우',
  },
  {
    label: '✅ 시나리오 3: 올바른 답변 (기울기)',
    emoji: '✅',
    category: '기울기 이해',
    step: 'LS05',
    question: 'y = −x + 3에서 기울기는?',
    answer: '기울기는 −1입니다.',
    wrongAnswer: '기울기는 −1입니다.',
    note: '정확한 답변',
  },
  {
    label: '📌 시나리오 4: 음수 기울기 오해 (M05)',
    emoji: '📌',
    category: '그래프 방향',
    step: 'LS08',
    question: 'y = −2x + 1 의 그래프 방향은?',
    answer: '음수 기울기이므로 그래프가 없습니다.',
    wrongAnswer: '음수 기울기이므로 그래프가 없습니다.',
    note: '음수 기울기 = 우하향 직선임을 모르는 경우',
  },
  {
    label: '📌 시나리오 5: 표에서 변화율 오류 (M06)',
    emoji: '📌',
    category: '변화율 계산',
    step: 'LS05',
    question: 'x: 0, 1, 2, 3 / y: 1, 4, 7, 10 에서 기울기는?',
    answer: '기울기 = 4 − 1 = 3',
    wrongAnswer: '기울기 = 4 − 1 = 3',
    note: '변화량 비율이 아닌 단순 차이를 기울기로 오해',
  },
  {
    label: '📌 시나리오 6: 문장제 → 함수식 오류 (M08)',
    emoji: '📌',
    category: '문장제 모델링',
    step: 'LS11',
    question: '처음에 10L 있는 수조에서 매분 2L씩 빠진다. t분 후 물의 양 y를 함수식으로 나타내면?',
    answer: 'y = 2t + 10',
    wrongAnswer: 'y = 2t + 10',
    note: '감소를 더하기로 잘못 작성한 경우',
  },
]

// 시나리오별 결과 매핑 키
const SCENARIO_KEYS = [
  'LS05-wrong-m03', 'LS07-wrong-m04', 'LS05-correct',
  'LS08-wrong-m05', 'LS05-wrong-m06', 'LS11-wrong-m08',
]

// ────────────────────────────────────────────────────────────────────────────
// 데모 결과 데이터
// ────────────────────────────────────────────────────────────────────────────
const DEMO_RESULTS: Record<string, TutorAnalysisResult> = {
  'LS05-wrong-m03': {
    student_id: 'student_001', current_step: 'LS05', step_concept: '기울기 개념', score: 20,
    mastery: { step_id: 'LS05', score: 20, attempts: 1, mastered: false, near_mastery: false, level: 'learning', message: '기초 개념을 다시 학습해야 합니다.' },
    misconceptions: [{ id: 'M03', type: 'INTERCEPT_AS_SLOPE', severity: 'high', description: 'Confusing y-intercept with slope', description_ko: 'y절편과 기울기를 혼동하는 오류', feedback_id: 'F-M03', learning_step: 'LS05' }],
    concept_gaps: [{ concept: 'Slope', from_misconception: 'M03', severity: 'high', remedial_step: 'LS05' }],
    feedbacks: [{ id: 'F-M03', misconception: 'M03', title: 'y절편과 기울기는 달라요!', message: 'y = ax + b에서 a는 기울기, b는 y절편입니다. y = 2x + 5에서 기울기는 x 앞의 계수인 2이고, y절편은 상수항인 5입니다.', hint: 'x 앞에 있는 숫자가 기울기(a), 더하거나 빼는 숫자가 y절편(b)!', next_step: 'LS05 기울기 개념 다시 학습' }],
    primary_feedback: { id: 'F-M03', misconception: 'M03', title: 'y절편과 기울기는 달라요!', message: 'y = ax + b에서 a는 기울기, b는 y절편입니다. y = 2x + 5에서 기울기는 x 앞의 계수인 2이고, y절편은 상수항인 5입니다.', hint: 'x 앞에 있는 숫자가 기울기(a), 더하거나 빼는 숫자가 y절편(b)!', next_step: 'LS05 기울기 개념 다시 학습' },
    next_step_recommendation: { current_step: 'LS05', next_step: 'LS05', recommendation: 'reinforce', reason: 'M03 오개념 해결 후 재학습 필요', score: 20, misconception_count: 1 },
    ontology_classes_activated: ['Slope', 'YIntercept', 'LinearFunction', 'Misconception', 'Feedback'],
    agent_reasoning: { perceived: '학생이 기울기와 y절편의 위치를 바꿔 답했습니다.', reasoned: 'M03(y절편을 기울기로 혼동) 오개념이 감지됩니다.', planned: 'M03 피드백 제공 후 LS05 재학습 권장', acted: '맞춤형 피드백과 힌트를 제공합니다.' },
  },
  'LS07-wrong-m04': {
    student_id: 'student_001', current_step: 'LS07', step_concept: '1차 함수 방정식', score: 30,
    mastery: { step_id: 'LS07', score: 30, attempts: 1, mastered: false, near_mastery: false, level: 'learning', message: '정비례와 1차 함수의 관계를 다시 학습하세요.' },
    misconceptions: [{ id: 'M04', type: 'PROPORTIONAL_EQUALS_LINEAR', severity: 'medium', description: 'Thinking proportional ≠ linear function', description_ko: '정비례 함수는 1차 함수가 아니라고 오해', feedback_id: 'F-M04', learning_step: 'LS07' }],
    concept_gaps: [{ concept: 'ProportionalFunction', from_misconception: 'M04', severity: 'medium', remedial_step: 'LS08' }],
    feedbacks: [{ id: 'F-M04', misconception: 'M04', title: '정비례는 1차 함수의 특별한 경우!', message: 'y = 3x는 y = ax + b에서 b = 0인 경우입니다. a ≠ 0이므로 1차 함수입니다! 정비례 함수는 1차 함수에 포함됩니다.', hint: 'b = 0인 1차 함수가 바로 정비례 함수입니다.', next_step: 'LS08 정비례 vs 1차 함수 학습' }],
    primary_feedback: { id: 'F-M04', misconception: 'M04', title: '정비례는 1차 함수의 특별한 경우!', message: 'y = 3x는 y = ax + b에서 b = 0인 경우입니다. a ≠ 0이므로 1차 함수입니다! 정비례 함수는 1차 함수에 포함됩니다.', hint: 'b = 0인 1차 함수가 바로 정비례 함수입니다.', next_step: 'LS08 정비례 vs 1차 함수 학습' },
    next_step_recommendation: { current_step: 'LS07', next_step: 'LS08', recommendation: 'advance', reason: 'M04 개념 보완을 위해 LS08 학습 권장', score: 30, misconception_count: 1 },
    ontology_classes_activated: ['ProportionalFunction', 'LinearFunction', 'Misconception', 'Feedback'],
    agent_reasoning: { perceived: '학생이 정비례를 1차 함수가 아니라고 답했습니다.', reasoned: 'M04(정비례 = 1차 함수 혼동) 오개념이 감지됩니다.', planned: 'M04 피드백 제공 후 LS08로 진행', acted: '정비례와 1차 함수의 관계를 설명하는 피드백을 제공합니다.' },
  },
  'LS05-correct': {
    student_id: 'student_001', current_step: 'LS05', step_concept: '기울기 개념', score: 100,
    mastery: { step_id: 'LS05', score: 100, attempts: 1, mastered: true, near_mastery: false, level: 'mastered', message: '완벽합니다! 기울기 개념을 마스터했습니다.' },
    misconceptions: [],
    concept_gaps: [],
    feedbacks: [{ id: 'F-correct', type: 'encouragement', title: '훌륭해요! 🎉', message: '기울기 −1을 정확히 구했습니다. y = −x + 3에서 x 앞의 계수가 −1이므로 기울기는 −1입니다. 음수 기울기이므로 그래프는 우하향합니다.', emoji: '🌟', personalized_message: '완벽한 답변입니다!' }],
    primary_feedback: { id: 'F-correct', type: 'encouragement', title: '훌륭해요! 🎉', message: '기울기 −1을 정확히 구했습니다.', emoji: '🌟' },
    next_step_recommendation: { current_step: 'LS05', next_step: 'LS06', recommendation: 'advance', reason: '기울기 마스터! y절편 학습으로 진행', score: 100, misconception_count: 0 },
    ontology_classes_activated: ['Slope', 'LinearFunction', 'Feedback'],
    agent_reasoning: { perceived: '학생이 기울기를 −1로 정확하게 답했습니다.', reasoned: '오개념이 없습니다. 완전 학습 상태입니다.', planned: '격려 피드백 후 LS06으로 진행', acted: '칭찬과 함께 다음 단계를 안내합니다.' },
  },
  'LS08-wrong-m05': {
    student_id: 'student_001', current_step: 'LS08', step_concept: '그래프 그리기', score: 15,
    mastery: { step_id: 'LS08', score: 15, attempts: 1, mastered: false, near_mastery: false, level: 'learning', message: '음수 기울기의 의미를 다시 학습하세요.' },
    misconceptions: [{ id: 'M05', type: 'NEGATIVE_SLOPE_INVALID', severity: 'high', description: 'Thinking negative slope means no graph', description_ko: '음수 기울기는 함수가 아니라고 오해', feedback_id: 'F-M05', learning_step: 'LS08' }],
    concept_gaps: [{ concept: 'NegativeSlope', from_misconception: 'M05', severity: 'high', remedial_step: 'LS08' }],
    feedbacks: [{ id: 'F-M05', misconception: 'M05', title: '음수 기울기 = 우하향 직선!', message: 'y = −2x + 1의 그래프는 기울기가 −2로 음수이므로 오른쪽으로 갈수록 아래로 내려가는 우하향 직선입니다. 음수 기울기는 존재하며, 단지 방향이 다를 뿐입니다!', hint: '기울기 > 0 → 우상향(↗), 기울기 < 0 → 우하향(↘)', next_step: 'LS08 그래프 방향 재학습' }],
    primary_feedback: { id: 'F-M05', misconception: 'M05', title: '음수 기울기 = 우하향 직선!', message: 'y = −2x + 1의 그래프는 기울기가 −2로 음수이므로 오른쪽으로 갈수록 아래로 내려가는 우하향 직선입니다.', hint: '기울기 > 0 → 우상향(↗), 기울기 < 0 → 우하향(↘)', next_step: 'LS08 그래프 방향 재학습' },
    next_step_recommendation: { current_step: 'LS08', next_step: 'LS08', recommendation: 'reinforce', reason: 'M05 오개념 해결 후 LS08 재학습', score: 15, misconception_count: 1 },
    ontology_classes_activated: ['Slope', 'LinearFunction', 'Graph', 'Misconception'],
    agent_reasoning: { perceived: '학생이 음수 기울기 = 함수 없음이라고 답했습니다.', reasoned: 'M05(음수 기울기는 함수 아님) 오개념이 감지됩니다.', planned: 'M05 피드백 제공 후 LS08 재학습 권장', acted: '음수 기울기의 의미를 시각적으로 설명합니다.' },
  },
  'LS05-wrong-m06': {
    student_id: 'student_001', current_step: 'LS05', step_concept: '변화율·기울기', score: 40,
    mastery: { step_id: 'LS05', score: 40, attempts: 1, mastered: false, near_mastery: false, level: 'learning', message: '기울기는 변화량 비율임을 다시 이해하세요.' },
    misconceptions: [{ id: 'M06', type: 'SLOPE_NOT_RATIO', severity: 'medium', description: 'Computing slope as difference instead of ratio', description_ko: '기울기를 변화량 비율이 아닌 단순 차이로 계산', feedback_id: 'F-M06', learning_step: 'LS05' }],
    concept_gaps: [{ concept: 'Slope', from_misconception: 'M06', severity: 'medium', remedial_step: 'LS05' }],
    feedbacks: [{ id: 'F-M06', misconception: 'M06', title: '기울기 = Δy ÷ Δx', message: '기울기 = (y의 변화량) ÷ (x의 변화량) 입니다. 표에서 x: 0→1 (Δx=1), y: 1→4 (Δy=3)이므로 기울기 = 3÷1 = 3입니다. 단순히 빼는 것이 아닙니다!', hint: '기울기 = Δy/Δx = (y2−y1)/(x2−x1)', next_step: 'LS05 변화율 개념 재학습' }],
    primary_feedback: { id: 'F-M06', misconception: 'M06', title: '기울기 = Δy ÷ Δx', message: '기울기 = (y의 변화량) ÷ (x의 변화량) 입니다. 이 문제에서는 Δy=3, Δx=1이므로 기울기 = 3입니다.', hint: '기울기 = Δy/Δx = (y2−y1)/(x2−x1)', next_step: 'LS05 변화율 개념 재학습' },
    next_step_recommendation: { current_step: 'LS05', next_step: 'LS05', recommendation: 'reinforce', reason: 'M06 오개념 해결을 위해 변화율 재학습', score: 40, misconception_count: 1 },
    ontology_classes_activated: ['Slope', 'RateOfChange', 'LinearFunction', 'Misconception'],
    agent_reasoning: { perceived: '학생이 기울기를 단순 차이(3)로 맞게 계산했지만 이유를 잘못 이해했습니다.', reasoned: 'M06(변화율 비율 오류) 오개념이 감지됩니다.', planned: 'Δy/Δx 개념을 명확히 하고 LS05 보강 학습', acted: '기울기의 정의를 단계별로 재설명합니다.' },
  },
  'LS11-wrong-m08': {
    student_id: 'student_001', current_step: 'LS11', step_concept: '실생활 함수 모델링', score: 25,
    mastery: { step_id: 'LS11', score: 25, attempts: 1, mastered: false, near_mastery: false, level: 'learning', message: '문장에서 함수식을 세울 때 방향성을 확인하세요.' },
    misconceptions: [{ id: 'M08', type: 'WORD_PROBLEM_DIRECTION', severity: 'high', description: 'Getting direction wrong in word problems', description_ko: '문장제에서 증가/감소 방향을 잘못 설정', feedback_id: 'F-M08', learning_step: 'LS11' }],
    concept_gaps: [{ concept: 'WordProblemModeling', from_misconception: 'M08', severity: 'high', remedial_step: 'LS11' }],
    feedbacks: [{ id: 'F-M08', misconception: 'M08', title: '감소하면 빼야 해요!', message: '매분 2L씩 "빠진다" = 감소 = 빼기입니다. 올바른 식은 y = −2t + 10 입니다. "빠진다", "줄어든다", "소비된다" → 마이너스 기울기!', hint: '"빠진다" → −, "늘어난다" → + 를 기억하세요!', next_step: 'LS11 문장제 모델링 재학습' }],
    primary_feedback: { id: 'F-M08', misconception: 'M08', title: '감소하면 빼야 해요!', message: '"빠진다" = 감소 = 마이너스. 올바른 식은 y = −2t + 10 입니다.', hint: '"빠진다" → −, "늘어난다" → + 를 기억하세요!', next_step: 'LS11 문장제 모델링 재학습' },
    next_step_recommendation: { current_step: 'LS11', next_step: 'LS11', recommendation: 'reinforce', reason: 'M08 오개념 해결을 위해 LS11 재학습', score: 25, misconception_count: 1 },
    ontology_classes_activated: ['WordProblem', 'LinearFunction', 'Slope', 'Misconception', 'Feedback'],
    agent_reasoning: { perceived: '학생이 감소 상황에서 더하기를 사용했습니다.', reasoned: 'M08(문장제 방향성 오류) 오개념이 감지됩니다.', planned: 'M08 피드백 제공 후 LS11 재학습 권장', acted: '증가/감소를 구분하는 방법을 명확히 안내합니다.' },
  },
}

// ────────────────────────────────────────────────────────────────────────────
// 세션 히스토리 타입
// ────────────────────────────────────────────────────────────────────────────
interface HistoryEntry {
  scenarioLabel: string
  score: number
  grade: string
  misconceptionCount: number
  at: string
}

function getGradeFromScore(score: number): string {
  if (score >= 90) return 'A'
  if (score >= 70) return 'B'
  if (score >= 50) return 'C'
  return 'D'
}

// ────────────────────────────────────────────────────────────────────────────
// 컴포넌트
// ────────────────────────────────────────────────────────────────────────────
export default function TutorPage() {
  const [selectedIdx, setSelectedIdx] = useState(0)
  const [customAnswer, setCustomAnswer] = useState('')
  const [result, setResult] = useState<TutorAnalysisResult | null>(null)
  const [loading, setLoading] = useState(false)
  const [history, setHistory] = useState<HistoryEntry[]>([])
  const [showHistory, setShowHistory] = useState(false)
  const [showReasoning, setShowReasoning] = useState(false)
  const [showOntology, setShowOntology] = useState(false)

  const { recordMisconceptions } = useProgressStore(s => ({
    recordMisconceptions: s.recordMisconceptions,
  }))

  const scenario = DEMO_SCENARIOS[selectedIdx]

  const handleAnalyze = async () => {
    setLoading(true)
    setResult(null)
    await new Promise(r => setTimeout(r, 900))
    const key = SCENARIO_KEYS[selectedIdx]
    const res = DEMO_RESULTS[key]
    setResult(res)

    // 오개념 progressStore에 기록
    if (res.misconceptions.length > 0) {
      recordMisconceptions('F2-1', res.misconceptions.map(m => m.id))
    }

    // 세션 히스토리 추가
    setHistory(prev => [
      {
        scenarioLabel: scenario.label,
        score: res.score,
        grade: getGradeFromScore(res.score),
        misconceptionCount: res.misconceptions.length,
        at: new Date().toLocaleTimeString('ko-KR', { hour: '2-digit', minute: '2-digit' }),
      },
      ...prev,
    ].slice(0, 10))

    setLoading(false)
  }

  const recColor =
    result?.next_step_recommendation.recommendation === 'advance'
      ? 'text-emerald-700 bg-emerald-50 border border-emerald-200'
      : result?.next_step_recommendation.recommendation === 'reinforce'
        ? 'text-blue-700 bg-blue-50 border border-blue-200'
        : 'text-orange-700 bg-orange-50 border border-orange-200'

  const avgScore = history.length > 0
    ? Math.round(history.reduce((s, h) => s + h.score, 0) / history.length)
    : null

  return (
    <div className="p-6 space-y-5">
      {/* ── 헤더 ──────────────────────────────────────────────────────── */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <Brain size={22} className="text-purple-600" />
          <div>
            <h1 className="text-xl font-bold text-slate-800">AI 튜터 — 답안 분석</h1>
            <p className="text-sm text-slate-500">학생 답안을 분석하여 오개념을 감지하고 맞춤 피드백을 제공합니다</p>
          </div>
        </div>
        {/* 세션 평균 점수 */}
        {avgScore !== null && (
          <div className="flex items-center gap-2 bg-purple-50 border border-purple-200 px-3 py-1.5 rounded-xl">
            <Star size={13} className="text-purple-500" />
            <span className="text-xs font-bold text-purple-700">세션 평균 {avgScore}점</span>
          </div>
        )}
      </div>

      {/* ── 세션 기록 토글 ──────────────────────────────────────────────── */}
      {history.length > 0 && (
        <div className="card border border-slate-200">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center justify-between w-full text-sm font-semibold text-slate-700 hover:text-primary-600 transition-colors"
          >
            <span className="flex items-center gap-2">
              <History size={14} />
              세션 기록 ({history.length}회)
            </span>
            {showHistory ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
          </button>
          {showHistory && (
            <div className="mt-3 space-y-1.5">
              {history.map((h, i) => (
                <div key={i} className="flex items-center gap-3 text-xs py-1 border-b border-slate-50 last:border-0">
                  <span className="text-slate-400 w-10">{h.at}</span>
                  <span className="flex-1 text-slate-600 truncate">{h.scenarioLabel.replace(/^📌|✅\s/, '')}</span>
                  <span className={`font-black w-12 text-right ${h.score >= 70 ? 'text-emerald-600' : h.score >= 40 ? 'text-orange-600' : 'text-red-600'}`}>
                    {h.score}점
                  </span>
                  <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${h.score >= 70 ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}`}>
                    {h.grade}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* ── 2열 레이아웃: 입력 | 결과 ─────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* ── 왼쪽: 시나리오 선택 + 답안 입력 ───────────────────────────── */}
        <div className="space-y-4">
          {/* 시나리오 목록 */}
          <div className="card">
            <p className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-1.5">
              <Target size={14} className="text-primary-500" />
              데모 시나리오 선택
              <span className="ml-auto text-xs text-slate-400 font-normal">({DEMO_SCENARIOS.length}가지)</span>
            </p>
            <div className="space-y-1.5">
              {DEMO_SCENARIOS.map((s, i) => (
                <button
                  key={i}
                  onClick={() => { setSelectedIdx(i); setResult(null); setCustomAnswer('') }}
                  className={`w-full text-left px-3 py-2.5 rounded-xl text-xs transition-all flex items-start gap-2 ${
                    selectedIdx === i
                      ? 'bg-primary-100 text-primary-700 font-semibold shadow-sm'
                      : 'hover:bg-slate-50 text-slate-600'
                  }`}
                >
                  <span className="text-base leading-none mt-0.5">{s.emoji}</span>
                  <div>
                    <div className="font-semibold">{s.category}</div>
                    <div className="text-slate-400 font-normal mt-0.5">{s.note}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* 문제 + 답안 입력 */}
          <div className="card space-y-3">
            <div>
              <p className="text-xs font-semibold text-slate-500 mb-1 flex items-center gap-1">
                <BookOpen size={11} /> 문제 ({scenario.step})
              </p>
              <p className="text-sm text-slate-800 bg-slate-50 rounded-xl p-3 border border-slate-200 leading-relaxed">
                {scenario.question}
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-500 mb-1">학생 답안 (수정 가능)</p>
              <textarea
                className="w-full h-20 resize-none border border-slate-200 rounded-xl p-3 text-sm text-slate-800 bg-white focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                value={customAnswer || scenario.wrongAnswer}
                onChange={e => setCustomAnswer(e.target.value)}
                placeholder="답안을 입력하세요..."
              />
              <p className="text-xs text-slate-400 mt-1">
                💡 답안을 직접 수정해보면 다른 오개념을 실험해볼 수 있습니다.
              </p>
            </div>
            <button
              onClick={handleAnalyze}
              disabled={loading}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold py-2.5 rounded-xl transition-colors flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {loading ? (
                <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> AI 분석 중...</>
              ) : (
                <><Send size={16} /> AI 튜터 분석 시작</>
              )}
            </button>
          </div>
        </div>

        {/* ── 오른쪽: 분석 결과 ─────────────────────────────────────────── */}
        <div className="space-y-4">
          {!result && !loading && (
            <div className="card flex flex-col items-center justify-center h-80 text-center bg-gradient-to-b from-slate-50 to-white">
              <Brain size={48} className="text-purple-200 mb-4" />
              <p className="text-slate-400 text-sm font-medium">시나리오를 선택하고</p>
              <p className="text-slate-400 text-sm">"AI 튜터 분석 시작"을 눌러보세요</p>
              <div className="mt-4 text-xs text-slate-300">
                실시간 오개념 감지 · 단계별 피드백 · 다음 학습 추천
              </div>
            </div>
          )}

          {loading && (
            <div className="card flex flex-col items-center justify-center h-80 text-center">
              <div className="relative mb-4">
                <div className="w-16 h-16 border-4 border-purple-100 border-t-purple-500 rounded-full animate-spin" />
                <Brain size={20} className="absolute inset-0 m-auto text-purple-400" />
              </div>
              <p className="text-slate-600 font-semibold">AI 분석 중...</p>
              <p className="text-slate-400 text-xs mt-1">온톨로지 추론 및 오개념 패턴 매칭</p>
            </div>
          )}

          {result && (
            <>
              {/* 점수 카드 */}
              <div className={`card flex items-center gap-4 ${result.score >= 70 ? 'bg-emerald-50 border border-emerald-200' : result.score >= 40 ? 'bg-orange-50 border border-orange-200' : 'bg-red-50 border border-red-200'}`}>
                {result.score >= 70
                  ? <CheckCircle size={36} className="text-emerald-500 flex-shrink-0" />
                  : <XCircle size={36} className="text-red-500 flex-shrink-0" />}
                <div className="flex-1">
                  <div className="text-3xl font-black text-slate-800">{result.score}점</div>
                  <div className="text-sm text-slate-600 mt-0.5">{result.mastery.message}</div>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                      result.mastery.mastered ? 'bg-emerald-100 text-emerald-700' :
                      result.mastery.near_mastery ? 'bg-blue-100 text-blue-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {result.mastery.level === 'mastered' ? '✓ 마스터' :
                       result.mastery.level === 'near_mastery' ? '거의 완료' : '학습 중'}
                    </span>
                    <span className="text-xs text-slate-400">{scenario.step} 단계</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className={`text-2xl font-black ${result.score >= 70 ? 'text-emerald-500' : result.score >= 40 ? 'text-orange-500' : 'text-red-500'}`}>
                    {getGradeFromScore(result.score)}
                  </div>
                  <div className="text-xs text-slate-400">등급</div>
                </div>
              </div>

              {/* 오개념 */}
              {result.misconceptions.length > 0 && (
                <div className="card">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertTriangle size={15} className="text-orange-500" />
                    <p className="text-sm font-bold text-slate-700">감지된 오개념</p>
                    <span className="ml-auto text-xs bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full font-bold">
                      {result.misconceptions.length}건
                    </span>
                  </div>
                  <div className="space-y-3">
                    {result.misconceptions.map(m => (
                      <MisconceptionAlert
                        key={m.id}
                        misconception={m}
                        feedback={result.feedbacks.find(f => f.id === m.feedback_id)}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* 주요 피드백 */}
              {result.primary_feedback && (
                <div className="card bg-amber-50 border border-amber-200">
                  <div className="flex items-start gap-2">
                    <Lightbulb size={16} className="text-amber-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-sm font-bold text-slate-800">{result.primary_feedback.title}</p>
                      <p className="text-sm text-slate-700 mt-1 leading-relaxed">{result.primary_feedback.message}</p>
                      {result.primary_feedback.hint && (
                        <div className="mt-2 text-xs text-amber-800 bg-amber-100 rounded-xl px-3 py-2 border border-amber-200">
                          💡 <span className="font-semibold">힌트:</span> {result.primary_feedback.hint}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* 다음 학습 추천 */}
              <div className={`card flex items-center gap-3 ${recColor}`}>
                <ArrowRight size={18} className="flex-shrink-0" />
                <div className="flex-1">
                  <p className="text-xs font-semibold uppercase tracking-wide opacity-70">다음 학습 단계 추천</p>
                  <p className="text-sm font-bold">{result.next_step_recommendation.next_step}</p>
                  <p className="text-xs opacity-80 mt-0.5">{result.next_step_recommendation.reason}</p>
                </div>
                <span className={`text-xs px-2 py-1 rounded-lg font-bold ${
                  result.next_step_recommendation.recommendation === 'advance'
                    ? 'bg-emerald-200 text-emerald-800'
                    : 'bg-blue-200 text-blue-800'
                }`}>
                  {result.next_step_recommendation.recommendation === 'advance' ? '↑ 진행' : '↩ 복습'}
                </span>
              </div>

              {/* Agentic AI 추론 과정 (토글) */}
              <div className="card">
                <button
                  onClick={() => setShowReasoning(!showReasoning)}
                  className="flex items-center justify-between w-full text-sm font-semibold text-slate-700 hover:text-primary-600 transition-colors"
                >
                  <span className="flex items-center gap-2">
                    <BarChart2 size={14} />
                    🤖 Agentic AI 추론 과정
                  </span>
                  {showReasoning ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                </button>
                {showReasoning && (
                  <div className="mt-3 space-y-2 text-xs text-slate-600">
                    {[
                      { label: '인식', value: result.agent_reasoning.perceived },
                      { label: '추론', value: result.agent_reasoning.reasoned },
                      { label: '계획', value: result.agent_reasoning.planned },
                      { label: '행동', value: result.agent_reasoning.acted },
                    ].map(({ label, value }) => (
                      <div key={label} className="flex gap-2">
                        <span className="font-bold text-slate-500 w-8 flex-shrink-0">{label}</span>
                        <span className="text-slate-600">{value}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* OWL 클래스 (토글) */}
              <div className="card">
                <button
                  onClick={() => setShowOntology(!showOntology)}
                  className="flex items-center justify-between w-full text-sm font-semibold text-slate-700 hover:text-primary-600 transition-colors"
                >
                  <span className="flex items-center gap-2">
                    <Award size={14} />
                    활성화된 OWL 클래스
                  </span>
                  {showOntology ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                </button>
                {showOntology && (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {result.ontology_classes_activated.map(c => (
                      <OntologyTag key={c} className_name={c} />
                    ))}
                  </div>
                )}
              </div>

              {/* 다시 시도 버튼 */}
              <button
                onClick={() => { setResult(null); setCustomAnswer('') }}
                className="w-full flex items-center justify-center gap-2 text-sm text-slate-500 hover:text-primary-600 border border-slate-200 rounded-xl py-2 hover:border-primary-300 transition-colors"
              >
                <RefreshCw size={13} /> 다른 시나리오 선택
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}
